import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { apiClient } from '../services/api';
import {
  UserFilters,
  UsersResponse,
  UserDetailResponse,
  AdminPlayerFilterMetaResponse,
  BanUserRequest,
  BanUserResponse,
  UnbanUserResponse,
  UpdateUserStatusRequest,
  UpdateUserStatusResponse,
  User,
  LoginCredentialsRequest,
  LoginCredentialsResponse,
  LoginRequest,
  LoginResponse,
  VerifyOtpRequest,
  SendCodeRequest,
  SendCodeResponse,
  TransactionFilters,
  TransactionsResponse,
  TransactionDetailResponse,
  TransactionStatsResponse,
  UpdateTransactionStatusRequest,
  UpdateTransactionStatusResponse,
  WalletResponse,
  WalletTransactionsResponse,
  WalletTransactionFilters,
  WithdrawalFilters,
  ApproveWithdrawalRequest,
  RejectWithdrawalRequest,
  CompleteWithdrawalRequest,
  WithdrawalPayoutLogResponse,
  TicketFilters,
  ReplyTicketRequest,
  UpdateTicketStatusRequest,
  AdminListFilters,
  AdminListResponse,
  CreateAdminRequest,
  UpdateAdminRequest,
  ResetAdminPasswordRequest,
  UpdateMySettingsRequest,
  UpdateMySettingsResponse,
  RequestEmailChangeRequest,
  RequestEmailChangeResponse,
  VerifyEmailChangeRequest,
  VerifyEmailChangeResponse,
  AdminDetailResponse,
  PermissionsListResponse,
  AdminPermissionsResponse,
  GrantPermissionsRequest,
  GrantPermissionsResponse,
  RevokePermissionsRequest,
  RevokePermissionsResponse,
  SetPermissionsRequest,
  SetPermissionsResponse,
  GameWalletFilters,
  GameWalletListResponse,
  GameWalletDetailResponse,
  GameWalletTransactionFilters,
  GameWalletTransactionsResponse,
  ProviderTransactionFilters,
  ProviderHealthResponse,
  ProviderSyncLogsFilters,
  ProviderSyncLogsResponse,
  SyncWalletResponse,
  GameWalletStatisticsResponse,
  GameProvidersResponse,
  GameProviderDetailResponse,
  CreateGameProviderRequest,
  CreateGameProviderResponse,
  UpdateGameProviderRequest,
  UpdateGameProviderResponse,
  ReorderGameProvidersResponse,
  DeleteGameProviderResponse,
  AgentBalancesFilters,
  AgentBalancesResponse,
  AgentBalanceHealthSnapshotsResponse,
  AgentBalancesSyncResponse,
  FullTransactionDetailResponse,
  RetrySyncRequest,
  RetrySyncResponse,
  FailedTransactionsFilters,
  FailedTransactionsResponse,
  RefundTransactionRequest,
  RefundTransactionResponse,
  RefundAllTransactionsRequest,
  RefundAllTransactionsResponse,
  VfxConfigurationResponse,
  UpdateVfxConfigRequest,
  PresetBackgroundsResponse,
  VfxScheduleListResponse,
  VfxScheduleDetailResponse,
  CreateVfxScheduleRequest,
  UpdateVfxScheduleRequest,
  TransactionLimitsConfigResponse,
  UpdateTransactionLimitsConfigRequest,
  FraudFlagFilters,
  FraudFlagsResponse,
  ResolveFraudFlagRequest,
  ResolveFraudFlagResponse,
  SignupBonusGrantsFilters,
  SignupBonusGrantsResponse,
  SignupBonusKpiResponse,
  ReservedBalanceDetailResponse,
  ManualGameDepositFilters,
  ManualGameDepositsResponse,
  ManualGameDepositDetailResponse,
  ConfirmManualDepositRequest,
  ConfirmManualDepositResponse,
  RejectManualDepositRequest,
  RejectManualDepositResponse,
  UpdateGameAccountRequest,
  UpdateGameAccountResponse,
  ProfitAnalysisResponse,
  AdjustBalanceRequest,
  AdjustBalanceResponse,
  PlayerAutoRefundSummaryResponse,
  ClearAutoRefundWarningRequest,
  ClearAutoRefundWarningResponse,
  DomainsResponse,
  DomainResponse,
  DomainGameMappingsResponse,
  CreateDomainRequest,
  UpdateDomainRequest,
  KioskHealthResponse,
  DomainAnalyticsResponse,
  AgentsListResponse,
  CreateCdnAgentRequest,
  CreateCdnAgentResponse,
  AdminAgentDetailResponse,
  CreateCdnShopRequest,
  CreateCdnShopResponse,
  AdminShopDetailResponse,
  AdminShopCreditAdjustRequest,
  AdminShopCreditAdjustResponse,
  AdminFinanceCorrectionRequest,
  AdminFinanceCorrectionResponse,
  CdnAgent,
  CdnShop,
  AdminAgentCashFundingRequest,
  AdminAgentCashFundingResponse,
  AdminAgentPaymentTransactionsResponse,
  AdminAgentWalletTransactionsResponse,
  CostReportResponse,
  SuperAgentsListResponse,
  CreateSuperAgentRequest,
  CreateSuperAgentResponse,
  SuperAgentWalletTransactionsResponse,
  ProviderFeeConfigsResponse,
  UpsertProviderFeeConfigsRequest,
  AdminCashiersListResponse,
  AdminCreateCashierRequest,
  AdminCreateCashierResponse,
  AdminCashierReportResponse,
  AdminCashierShiftHistoryResponse,
  AdminFraudIndicatorsResponse,
} from '../types';
import { SUCCESS_MESSAGES } from '../constants';

export const QUERY_KEYS = {
  AUTH: 'auth',
  USERS: 'users',
  PLAYER_FILTER_META: 'playerFilterMeta',
  USER_DETAILS: 'userDetails',
  TRANSACTIONS: 'transactions',
  TRANSACTION_DETAILS: 'transactionDetails',
  TRANSACTION_ALLOCATIONS: 'transactionAllocations',
  TRANSACTION_STATS: 'transactionStats',
  WALLET: 'wallet',
  WALLET_TRANSACTIONS: 'walletTransactions',
  WITHDRAWALS: 'withdrawals',
  TICKETS: 'tickets',
  TICKET_DETAILS: 'ticketDetails',
  SEARCH_TICKETS: 'searchTickets',
  SUPPORT_STATS: 'supportStats',
  CATEGORIES: 'categories',
  ADMINS: 'admins',
  CURRENT_ADMIN: 'currentAdmin',
  PERMISSIONS: 'permissions',
  ADMIN_PERMISSIONS: 'adminPermissions',
  GAME_WALLETS: 'gameWallets',
  GAME_WALLET_DETAIL: 'gameWalletDetail',
  GAME_WALLET_TRANSACTIONS: 'gameWalletTransactions',
  PROVIDER_TRANSACTIONS: 'providerTransactions',
  PROVIDER_HEALTH: 'providerHealth',
  PROVIDER_SYNC_LOGS: 'providerSyncLogs',
  GAME_WALLET_STATISTICS: 'gameWalletStatistics',
  GAME_PROVIDERS: 'gameProviders',
  GAME_PROVIDER_DETAIL: 'gameProviderDetail',
  AGENT_BALANCES: 'agentBalances',
  AGENT_BALANCE_HEALTH_SNAPSHOTS: 'agentBalanceHealthSnapshots',
  FULL_TRANSACTION_DETAIL: 'fullTransactionDetail',
  FAILED_TRANSACTIONS: 'failedTransactions',
  VFX_CONFIG: 'vfxConfig',
  PRESET_BACKGROUNDS: 'presetBackgrounds',
  VFX_SCHEDULES: 'vfxSchedules',
  VFX_SCHEDULE: 'vfxSchedule',
  TRANSACTION_LIMITS_CONFIG: 'transactionLimitsConfig',
  FRAUD_FLAGS: 'fraudFlags',
  SIGNUP_BONUS_GRANTS: 'signupBonusGrants',
  SIGNUP_BONUS_KPI: 'signupBonusKpi',
  RESERVED_BALANCE_DETAIL: 'reservedBalanceDetail',
  MANUAL_GAME_DEPOSITS: 'manualGameDeposits',
  MANUAL_GAME_DEPOSIT_DETAIL: 'manualGameDepositDetail',
  WITHDRAWAL_PROFIT_ANALYSIS: 'withdrawalProfitAnalysis',
  PLAYER_AUTO_REFUND_SUMMARY: 'playerAutoRefundSummary',
  DOMAINS: 'domains',
  KIOSK_HEALTH: 'kioskHealth',
  DOMAIN_ANALYTICS: 'domainAnalytics',
  DOMAIN_GAME_MAPPINGS: 'domainGameMappings',
  CDN_AGENTS: 'cdnAgents',
  SUPER_AGENTS: 'superAgents',
  SUPER_AGENT_DETAIL: 'superAgentDetail',
  SUPER_AGENT_WALLET_TRANSACTIONS: 'superAgentWalletTransactions',
  SUPER_AGENT_GAME_MAPPINGS: 'superAgentGameMappings',
  ADMIN_AGENT_DETAIL: 'adminAgentDetail',
  ADMIN_AGENT_PAYMENT_TX: 'adminAgentPaymentTx',
  ADMIN_AGENT_WALLET_TX: 'adminAgentWalletTx',
  COST_REPORT: 'costReport',
  ADMIN_SHOP_DETAIL: 'adminShopDetail',
  ADMIN_SHOP_DETAIL_BY_SHOP_ID: 'adminShopDetailByShopId',
  PROVIDER_FEE_CONFIGS: 'providerFeeConfigs',
  ADMIN_CASHIERS: 'adminCashiers',
  ADMIN_CASHIER_REPORT: 'adminCashierReport',
  ADMIN_CASHIER_SHIFTS: 'adminCashierShifts',
  ADMIN_FRAUD_INDICATORS: 'adminFraudIndicators',
  USD_WALLET_SUMMARY: 'usdWalletSummary',
  USD_WALLET_LEDGER: 'usdWalletLedger',
  USD_WALLET_LEDGER_ENTRY: 'usdWalletLedgerEntry',
  USD_WALLET_LINKED_CREDITS: 'usdWalletLinkedCredits',
  CREDIT_WALLET_SUMMARY: 'creditWalletSummary',
  CREDIT_WALLET_LEDGER: 'creditWalletLedger',
  INTEGRATION_GAME_LOGS: 'integrationGameLogs',
  AUDIT_LOGS: 'adminAuditLogs',
  INTEGRATION_PAYMENT_LOGS: 'integrationPaymentLogs',
  INTEGRATION_WEBHOOK_LOGS: 'integrationWebhookLogs',
  INTEGRATION_GAME_LOG_DETAIL: 'integrationGameLogDetail',
  INTEGRATION_PAYMENT_LOG_DETAIL: 'integrationPaymentLogDetail',
  INTEGRATION_WEBHOOK_LOG_DETAIL: 'integrationWebhookLogDetail',
} as const;

// Utility function to parse error messages
const parseErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred. Please try again.';
};

// Auth Hooks (Admin Login V2)
export const useLoginCredentials = () => {
  return useMutation<LoginCredentialsResponse, Error, LoginCredentialsRequest>(
    (data) => apiClient.loginCredentials(data),
    {
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useVerifyOtp = () => {
  return useMutation<LoginResponse, Error, VerifyOtpRequest>(
    (data) => apiClient.verifyOtp(data),
    {
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

/** @deprecated Legacy flow - use useLoginCredentials + useVerifyOtp */
export const useSendEmailCode = () => {
  return useMutation<SendCodeResponse, Error, SendCodeRequest>(
    (data) =>
      apiClient.loginCredentials({
        usernameOrEmail: data.email,
        password: (data as { email: string; password?: string }).password ?? '',
      }) as Promise<SendCodeResponse>,
    {
      onSuccess: (data) => {
        toast.success(data?.message || SUCCESS_MESSAGES.EMAIL_CODE_SENT);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

/** @deprecated Legacy flow - use useVerifyOtp with pendingToken */
export const useLogin = () => {
  return useMutation<LoginResponse, Error, LoginRequest>(
    (data) => apiClient.login(data.email, data.otp),
    {
      onSuccess: (data) => {
        if (data?.data?.token) {
          localStorage.setItem('admin_auth_token', data.data.token);
        }
        if (data?.data?.admin) {
          localStorage.setItem('admin_user_data', JSON.stringify(data.data.admin));
        }
        toast.success(data.message || SUCCESS_MESSAGES.LOGIN_SUCCESS);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useCurrentAdmin = () => {
  return useQuery<AdminDetailResponse, Error>(
    QUERY_KEYS.CURRENT_ADMIN,
    () => apiClient.getCurrentAdmin(),
  );
};

export const useAdmins = (filters: AdminListFilters) => {
  return useQuery<AdminListResponse, Error>(
    [QUERY_KEYS.ADMINS, filters],
    () => apiClient.getAdmins(filters),
    {
      keepPreviousData: true,
    }
  );
};

export const useCreateAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation<AdminDetailResponse, Error, CreateAdminRequest>(
    (payload) => apiClient.createAdmin(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.ADMINS);
        toast.success(SUCCESS_MESSAGES.ADMIN_CREATED);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useUpdateAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation<AdminDetailResponse, Error, { adminId: string; data: UpdateAdminRequest }>(
    ({ adminId, data }) => apiClient.updateAdmin(adminId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.ADMINS);
        toast.success(SUCCESS_MESSAGES.ADMIN_UPDATED);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useDeleteAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; message: string }, Error, { adminId: string }>(
    ({ adminId }) => apiClient.deleteAdmin(adminId),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.ADMINS);
        toast.success(data?.message || SUCCESS_MESSAGES.ADMIN_DELETED);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useResetAdminPassword = () => {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; message: string }, Error, { adminId: string; data: ResetAdminPasswordRequest }>(
    ({ adminId, data }) => apiClient.resetAdminPassword(adminId, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.ADMINS);
        toast.success(data?.message || SUCCESS_MESSAGES.ADMIN_PASSWORD_RESET);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useUpdateMySettings = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateMySettingsResponse, Error, UpdateMySettingsRequest>(
    (data) => apiClient.updateMySettings(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.CURRENT_ADMIN);
        toast.success(data?.message || 'Settings updated.');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useRequestEmailChange = () => {
  return useMutation<RequestEmailChangeResponse, Error, RequestEmailChangeRequest>(
    (data) => apiClient.requestEmailChange(data),
    {
      onSuccess: (data) => {
        toast.success(data?.message || 'OTP sent to new email.');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useVerifyEmailChange = () => {
  const queryClient = useQueryClient();

  return useMutation<VerifyEmailChangeResponse, Error, VerifyEmailChangeRequest>(
    (data) => apiClient.verifyEmailChange(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.CURRENT_ADMIN);
        toast.success(data?.message || 'Email updated.');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// User Hooks
export const useUsers = (filters: UserFilters) => {
  return useQuery<UsersResponse, Error>(
    [QUERY_KEYS.USERS, filters],
    () => apiClient.getUsers(filters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const usePlayerFilterMeta = () => {
  return useQuery<AdminPlayerFilterMetaResponse, Error>(
    [QUERY_KEYS.PLAYER_FILTER_META],
    () => apiClient.getPlayerFilterMeta(),
    {
      staleTime: 10 * 60 * 1000,
    }
  );
};

export const useUserDetails = (userId: string, options?: { enabled?: boolean }) => {
  const enabled = options?.enabled === false ? false : !!userId;
  return useQuery<UserDetailResponse, Error>(
    [QUERY_KEYS.USER_DETAILS, userId],
    () => apiClient.getUserDetails(userId),
    {
      enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useBanUser = () => {
  const queryClient = useQueryClient();

  return useMutation<BanUserResponse, Error, { userId: string; data: BanUserRequest }>(
    ({ userId, data }) => apiClient.banUser(userId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.USERS);
        queryClient.invalidateQueries(QUERY_KEYS.USER_DETAILS);
        toast.success(SUCCESS_MESSAGES.USER_BANNED);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useTerminatePlayerPreview = (userId: string, enabled: boolean) => {
  return useQuery(
    [QUERY_KEYS.USER_DETAILS, userId, 'terminate-preview'],
    () => apiClient.getTerminatePlayerPreview(userId),
    {
      enabled: enabled && !!userId,
      staleTime: 0,
    },
  );
};

export const useTerminatePlayer = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ userId, reason }: { userId: string; reason: string }) =>
      apiClient.terminatePlayer(userId, { reason: reason.trim(), confirm: true }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.USERS);
        queryClient.invalidateQueries(QUERY_KEYS.USER_DETAILS);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const useCloseAndReleasePlayer = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ userId, reason }: { userId: string; reason: string }) =>
      apiClient.closeAndReleasePlayer(userId, { reason: reason.trim(), confirm: true }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.USERS);
        queryClient.invalidateQueries(QUERY_KEYS.USER_DETAILS);
      },
    },
  );
};

export const useUnbanUser = () => {
  const queryClient = useQueryClient();

  return useMutation<UnbanUserResponse, Error, { userId: string }>(
    ({ userId }) => apiClient.unbanUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.USERS);
        queryClient.invalidateQueries(QUERY_KEYS.USER_DETAILS);
        toast.success(SUCCESS_MESSAGES.USER_UNBANNED);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

const patchUserStatusInCache = (user: User, status: User['status']): User => {
  const next: User = { ...user, status };
  if (status === 'banned') return { ...next, banned: true };
  return next;
};

export const useUpdateUserStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateUserStatusResponse, Error, { userId: string; data: UpdateUserStatusRequest }>(
    ({ userId, data }) => apiClient.updateUserStatus(userId, data),
    {
      onSuccess: (res, { userId, data }) => {
        const status = (res?.data?.status ?? data.status) as User['status'];

        queryClient.setQueriesData<UsersResponse | undefined>(
          [QUERY_KEYS.USERS],
          (old) => {
            if (!old?.data?.users) return old;
            return {
              ...old,
              data: {
                ...old.data,
                users: old.data.users.map((u) =>
                  u.id === userId ? patchUserStatusInCache(u, status) : u
                ),
              },
            };
          }
        );

        queryClient.setQueryData<UserDetailResponse | undefined>(
          [QUERY_KEYS.USER_DETAILS, userId],
          (old) => {
            if (!old?.data?.user) return old;
            return {
              ...old,
              data: {
                ...old.data,
                user: patchUserStatusInCache(old.data.user, status),
              },
            };
          }
        );

        queryClient.invalidateQueries(QUERY_KEYS.USERS);
        queryClient.invalidateQueries(QUERY_KEYS.USER_DETAILS);

        if (status === 'suspended') toast.success('Player suspended');
        else if (status === 'active') toast.success('Player status set to active');
        else toast.success('Player status updated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// Transaction Hooks
export const useTransactions = (filters: TransactionFilters) => {
  return useQuery<TransactionsResponse, Error>(
    [QUERY_KEYS.TRANSACTIONS, filters],
    () => apiClient.getTransactions(filters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useTransactionStats = (filters?: Partial<TransactionFilters>) => {
  return useQuery<TransactionStatsResponse, Error>(
    [QUERY_KEYS.TRANSACTION_STATS, filters],
    () => apiClient.getTransactionStats(filters),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useTransactionDetail = (transactionId: number) => {
  return useQuery<TransactionDetailResponse, Error>(
    [QUERY_KEYS.TRANSACTION_DETAILS, transactionId],
    () => apiClient.getTransactionDetail(transactionId),
    {
      enabled: !!transactionId,
    }
  );
};

export const useTransactionAllocations = (transactionId: number, enabled: boolean) => {
  return useQuery<import('../types').TransactionAllocationsResponse, Error>(
    [QUERY_KEYS.TRANSACTION_ALLOCATIONS, transactionId],
    () => apiClient.getTransactionAllocations(transactionId),
    {
      enabled: enabled && !!transactionId,
      staleTime: 60 * 1000,
    }
  );
};

export const useUpdateTransactionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateTransactionStatusResponse, Error, { transactionId: number; data: UpdateTransactionStatusRequest }>(
    ({ transactionId, data }) => apiClient.updateTransactionStatus(transactionId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.TRANSACTIONS);
        queryClient.invalidateQueries(QUERY_KEYS.TRANSACTION_DETAILS);
        queryClient.invalidateQueries(QUERY_KEYS.TRANSACTION_STATS);
        toast.success('Transaction status updated successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// Wallet Hooks
export const useUserWallet = (userId: string) => {
  return useQuery<WalletResponse, Error>(
    [QUERY_KEYS.WALLET, userId],
    () => apiClient.getUserWallet(userId),
    {
      enabled: !!userId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useWalletTransactions = (
  userId: string,
  filters: WalletTransactionFilters
) => {
  return useQuery<WalletTransactionsResponse, Error>(
    [QUERY_KEYS.WALLET_TRANSACTIONS, userId, filters],
    () => apiClient.getWalletTransactions(userId, filters),
    {
      enabled: !!userId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useReservedBalanceDetail = (userId: string, enabled: boolean = false) => {
  return useQuery<ReservedBalanceDetailResponse, Error>(
    [QUERY_KEYS.RESERVED_BALANCE_DETAIL, userId],
    () => apiClient.getReservedBalanceDetail(userId),
    {
      enabled: !!userId && enabled,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

// ===== WITHDRAWAL REQUEST HOOKS =====

export const useWithdrawals = (filters: WithdrawalFilters) => {
  return useQuery({
    queryKey: [QUERY_KEYS.WITHDRAWALS, filters],
    queryFn: () => apiClient.getWithdrawals(filters),
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useApproveWithdrawal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, data }: { transactionId: string; data: ApproveWithdrawalRequest }) =>
      apiClient.approveWithdrawal(transactionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WITHDRAWALS] });
    },
  });
};

export const useRejectWithdrawal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, data }: { transactionId: string; data: RejectWithdrawalRequest }) =>
      apiClient.rejectWithdrawal(transactionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WITHDRAWALS] });
    },
  });
};

export const useCompleteWithdrawal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, data }: { transactionId: string; data: CompleteWithdrawalRequest }) =>
      apiClient.completeWithdrawal(transactionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WITHDRAWALS] });
    },
  });
};

export const useCompleteGameWithdrawalRecovery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, data }: { transactionId: string; data: CompleteWithdrawalRequest }) =>
      apiClient.completeGameWithdrawalRecovery(transactionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WITHDRAWALS] });
    },
  });
};


export const useWithdrawalPayoutLog = (transactionId: string) => {
  return useQuery<WithdrawalPayoutLogResponse, Error>({
    queryKey: ['withdrawalPayoutLog', transactionId],
    queryFn: () => apiClient.getWithdrawalPayoutLog(transactionId),
    enabled: !!transactionId,
    staleTime: 30000, // 30 seconds
  });
};

export const useRetryWithdrawalPayout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionId: string) => apiClient.retryWithdrawalPayout(transactionId),
    onSuccess: (_, transactionId) => {
      queryClient.invalidateQueries({ queryKey: ['withdrawalPayoutLog', transactionId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WITHDRAWALS] });
      toast.success('Payout retry initiated successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to retry payout';
      toast.error(errorMessage);
    },
  });
};

// Support Ticket Hooks
export const useTickets = (filters?: TicketFilters) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TICKETS, filters],
    queryFn: () => apiClient.getTickets(filters),
    keepPreviousData: true,
    staleTime: 30000, // 30 seconds
  });
};

export const useTicket = (ticketNumber: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TICKET_DETAILS, ticketNumber],
    queryFn: () => apiClient.getTicket(ticketNumber),
    enabled: !!ticketNumber,
    retry: false,
    staleTime: 30000, // 30 seconds
    refetchInterval: 10000, // Poll every 10 seconds
  });
};

export const useSearchTickets = (params: {
  email?: string;
  phone?: string;
  cursor?: string;
  direction?: 'next' | 'prev';
  limit?: number;
  status?: string;
  category?: string;
  tier?: string;
}) => {
  const hasSearchTerm = !!(params.email || params.phone || params.category);
  return useQuery({
    queryKey: [QUERY_KEYS.SEARCH_TICKETS, params.email, params.phone, params.cursor, params.direction, params.status, params.category, params.tier],
    queryFn: () => apiClient.searchTickets(params),
    enabled: hasSearchTerm,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

export const useSupportStats = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.SUPPORT_STATS],
    queryFn: () => apiClient.getSupportStats(),
    staleTime: 60000, // 1 minute
  });
};

export const useCategories = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.CATEGORIES],
    queryFn: () => apiClient.getCategories(),
    staleTime: 300000, // 5 minutes
  });
};

export const useReplyTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketNumber, data }: { ticketNumber: string; data: ReplyTicketRequest }) =>
      apiClient.replyTicket(ticketNumber, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKET_DETAILS, variables.ticketNumber] });
    },
  });
};

export const useUpdateTicketStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketNumber, data }: { ticketNumber: string; data: UpdateTicketStatusRequest }) =>
      apiClient.updateTicketStatus(ticketNumber, data),
    onSuccess: (_, variables) => {
      // Refresh all ticket-related queries: list, search, details, stats
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SEARCH_TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKET_DETAILS, variables.ticketNumber] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SUPPORT_STATS] });
    },
  });
};

export const useMarkTicketDone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketNumber: string) => apiClient.markTicketDone(ticketNumber),
    onSuccess: (_, ticketNumber) => {
      // Marking as "done" (now mapped to CLOSED) should also refresh all ticket views
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SEARCH_TICKETS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKET_DETAILS, ticketNumber] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SUPPORT_STATS] });
    },
  });
};

// Permission management hooks
export const useAllPermissions = (category?: string) => {
  return useQuery<PermissionsListResponse, Error>(
    [QUERY_KEYS.PERMISSIONS, category],
    () => apiClient.getAllPermissions(category),
    {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );
};

export const useAdminPermissions = (adminId: string) => {
  return useQuery<AdminPermissionsResponse, Error>(
    [QUERY_KEYS.ADMIN_PERMISSIONS, adminId],
    () => apiClient.getAdminPermissions(adminId),
    {
      enabled: !!adminId,
    }
  );
};

export const useGrantPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation<GrantPermissionsResponse, Error, { adminId: string; data: GrantPermissionsRequest }>(
    ({ adminId, data }) => apiClient.grantPermissions(adminId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_PERMISSIONS, variables.adminId]);
        queryClient.invalidateQueries([QUERY_KEYS.ADMINS]);
        queryClient.invalidateQueries([QUERY_KEYS.CURRENT_ADMIN]);
        toast.success('Permissions granted successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useRevokePermissions = () => {
  const queryClient = useQueryClient();

  return useMutation<RevokePermissionsResponse, Error, { adminId: string; data: RevokePermissionsRequest }>(
    ({ adminId, data }) => apiClient.revokePermissions(adminId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_PERMISSIONS, variables.adminId]);
        queryClient.invalidateQueries([QUERY_KEYS.ADMINS]);
        queryClient.invalidateQueries([QUERY_KEYS.CURRENT_ADMIN]);
        toast.success('Permissions revoked successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useSetPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation<SetPermissionsResponse, Error, { adminId: string; data: SetPermissionsRequest }>(
    ({ adminId, data }) => apiClient.setPermissions(adminId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_PERMISSIONS, variables.adminId]);
        queryClient.invalidateQueries([QUERY_KEYS.ADMINS]);
        queryClient.invalidateQueries([QUERY_KEYS.CURRENT_ADMIN]);
        toast.success('Permissions updated successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// ===== GAME WALLET HOOKS =====

export const useGameWallets = (filters: GameWalletFilters) => {
  return useQuery<GameWalletListResponse, Error>(
    [QUERY_KEYS.GAME_WALLETS, filters],
    () => apiClient.getGameWallets(filters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useGameWalletDetail = (id: string) => {
  return useQuery<GameWalletDetailResponse, Error>(
    [QUERY_KEYS.GAME_WALLET_DETAIL, id],
    () => apiClient.getGameWalletDetail(id),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useGameWalletTransactions = (
  id: string,
  filters: GameWalletTransactionFilters
) => {
  return useQuery<GameWalletTransactionsResponse, Error>(
    [QUERY_KEYS.GAME_WALLET_TRANSACTIONS, id, filters],
    () => apiClient.getGameWalletTransactions(id, filters),
    {
      enabled: !!id,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useProviderGameWalletTransactions = (filters: ProviderTransactionFilters) => {
  return useQuery<GameWalletTransactionsResponse, Error>(
    [QUERY_KEYS.PROVIDER_TRANSACTIONS, filters],
    () => apiClient.getProviderGameWalletTransactions(filters),
    {
      enabled: !!filters.gameProviderId,
      keepPreviousData: true,
      staleTime: 30 * 1000,
    }
  );
};

export const useProviderHealth = (gameProviderId: string, hours = 24) => {
  return useQuery<ProviderHealthResponse, Error>(
    [QUERY_KEYS.PROVIDER_HEALTH, gameProviderId, hours],
    () => apiClient.getProviderHealth(gameProviderId, hours),
    {
      enabled: !!gameProviderId,
      staleTime: 60 * 1000,
    }
  );
};

export const useProviderSyncLogs = (filters: ProviderSyncLogsFilters) => {
  return useQuery<ProviderSyncLogsResponse, Error>(
    [QUERY_KEYS.PROVIDER_SYNC_LOGS, filters],
    () => apiClient.getProviderSyncLogs(filters),
    {
      enabled: !!filters.gameProviderId,
      keepPreviousData: true,
      staleTime: 30 * 1000,
    }
  );
};

export const useSyncGameWallet = () => {
  const queryClient = useQueryClient();

  return useMutation<SyncWalletResponse, Error, string>(
    (id) => apiClient.syncGameWallet(id),
    {
      onSuccess: (data, id) => {
        queryClient.invalidateQueries([QUERY_KEYS.GAME_WALLET_DETAIL, id]);
        queryClient.invalidateQueries([QUERY_KEYS.GAME_WALLETS]);
        queryClient.invalidateQueries([QUERY_KEYS.GAME_WALLET_STATISTICS]);
        toast.success(data?.message || 'Sync initiated successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useGameWalletStatistics = (filters?: Partial<GameWalletFilters>) => {
  return useQuery<GameWalletStatisticsResponse, Error>(
    [QUERY_KEYS.GAME_WALLET_STATISTICS, filters],
    () => apiClient.getGameWalletStatistics(filters),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useGameProviders = () => {
  return useQuery<GameProvidersResponse, Error>(
    QUERY_KEYS.GAME_PROVIDERS,
    () => apiClient.getGameProviders(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes - providers don't change often
    }
  );
};

// Providers for the Game Wallets monitoring page — walletManagement-gated, safe fields only.
export const useGameWalletProviders = () => {
  return useQuery<GameProvidersResponse, Error>(
    ['game-wallet-providers'],
    () => apiClient.getGameWalletProviders(),
    {
      staleTime: 10 * 60 * 1000,
    }
  );
};

export const useGameProviderDetail = (id: string) => {
  return useQuery<GameProviderDetailResponse, Error>(
    [QUERY_KEYS.GAME_PROVIDER_DETAIL, id],
    () => apiClient.getGameProviderDetail(id),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useCreateGameProvider = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateGameProviderResponse, Error, CreateGameProviderRequest>(
    (data) => apiClient.createGameProvider(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.GAME_PROVIDERS);
        toast.success('Game provider created successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useUpdateGameProvider = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateGameProviderResponse, Error, { id: string; data: UpdateGameProviderRequest }>(
    ({ id, data }) => apiClient.updateGameProvider(id, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(QUERY_KEYS.GAME_PROVIDERS);
        queryClient.invalidateQueries([QUERY_KEYS.GAME_PROVIDER_DETAIL, variables.id]);
        toast.success('Game provider updated successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useReorderGameProviders = () => {
  const queryClient = useQueryClient();

  return useMutation<ReorderGameProvidersResponse, Error, string[]>(
    (orderedIds) => apiClient.reorderGameProviders(orderedIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.GAME_PROVIDERS);
        toast.success('Kiosk order saved (Home, Deposit, Withdrawal)');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useDeleteGameProvider = () => {
  const queryClient = useQueryClient();

  return useMutation<DeleteGameProviderResponse, Error, string>(
    (id) => apiClient.deleteGameProvider(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.GAME_PROVIDERS);
        toast.success('Game provider deleted successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// ===== AGENT BALANCES HOOKS =====

export const useAgentBalanceHealthSnapshots = (hours = 24, enabled = true) => {
  return useQuery<AgentBalanceHealthSnapshotsResponse, Error>(
    [QUERY_KEYS.AGENT_BALANCE_HEALTH_SNAPSHOTS, hours],
    () => apiClient.getAgentBalanceHealthSnapshots(hours),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000,
      enabled,
    }
  );
};

export const useAgentBalances = (filters?: AgentBalancesFilters, enabled = true) => {
  return useQuery<AgentBalancesResponse, Error>(
    [QUERY_KEYS.AGENT_BALANCES, filters],
    () => apiClient.getAgentBalances(filters),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 minutes - balance syncs every 10 min
      enabled,
    }
  );
};

export const useSyncAgentBalances = () => {
  const queryClient = useQueryClient();

  return useMutation<AgentBalancesSyncResponse, Error, void>(
    () => apiClient.syncAgentBalances(),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.AGENT_BALANCES);
        const msg = data?.data
          ? `Synced ${data.data.synced} game(s), ${data.data.failed} failed.`
          : 'Sync completed.';
        toast.success(msg);
      },
      onError: (error: any) => {
        const status = error?.response?.status;
        const msg = error?.response?.data?.message || parseErrorMessage(error);
        if (status === 409) {
          toast.error('A sync is already running. Please try again later.');
        } else if (status === 503) {
          toast.error('Sync unavailable (Redis). Please try again later.');
        } else {
          toast.error(msg);
        }
      },
    }
  );
};

// ===== FULL TRANSACTION & RETRY SYNC HOOKS =====

export const useFullTransactionDetail = (transactionId: string) => {
  return useQuery<FullTransactionDetailResponse, Error>(
    [QUERY_KEYS.FULL_TRANSACTION_DETAIL, transactionId],
    () => apiClient.getFullTransactionDetail(transactionId),
    {
      enabled: !!transactionId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useRetrySyncTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation<RetrySyncResponse, Error, { transactionId: string; data: RetrySyncRequest }>(
    ({ transactionId, data }) => apiClient.retrySyncTransaction(transactionId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries([QUERY_KEYS.FULL_TRANSACTION_DETAIL, variables.transactionId]);
        queryClient.invalidateQueries(QUERY_KEYS.FAILED_TRANSACTIONS);
        queryClient.invalidateQueries(QUERY_KEYS.TRANSACTIONS);
        toast.success('Sync retry initiated successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useFailedTransactions = (filters: FailedTransactionsFilters) => {
  return useQuery<FailedTransactionsResponse, Error>(
    [QUERY_KEYS.FAILED_TRANSACTIONS, filters],
    () => apiClient.getFailedTransactions(filters),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useRefundTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation<RefundTransactionResponse, Error, { transactionId: number; data: RefundTransactionRequest }>(
    ({ transactionId, data }) => apiClient.refundTransaction(transactionId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(QUERY_KEYS.TRANSACTIONS);
        queryClient.invalidateQueries(QUERY_KEYS.GAME_WALLET_TRANSACTIONS);
        queryClient.invalidateQueries(QUERY_KEYS.FAILED_TRANSACTIONS);
        toast.success('Transaction refunded successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useRefundAllTransactions = () => {
  const queryClient = useQueryClient();

  return useMutation<RefundAllTransactionsResponse, Error, RefundAllTransactionsRequest>(
    (data) => apiClient.refundAllTransactions(data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(QUERY_KEYS.TRANSACTIONS);
        queryClient.invalidateQueries(QUERY_KEYS.GAME_WALLET_TRANSACTIONS);
        queryClient.invalidateQueries(QUERY_KEYS.FAILED_TRANSACTIONS);
        queryClient.invalidateQueries(QUERY_KEYS.GAME_WALLET_DETAIL);
        toast.success(`Successfully refunded ${response.data?.refundedCount || 0} transaction(s)`);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// ===== VFX CONFIGURATION HOOKS =====

export const useVfxConfig = () => {
  return useQuery<VfxConfigurationResponse, Error>(
    QUERY_KEYS.VFX_CONFIG,
    () => apiClient.getVfxConfig(),
    {
      staleTime: 60 * 1000, // 1 minute - align with client visibility (changes within 60 sec)
    }
  );
};

export const useUpdateVfxConfig = () => {
  const queryClient = useQueryClient();

  return useMutation<VfxConfigurationResponse, Error, UpdateVfxConfigRequest>(
    (data) => apiClient.updateVfxConfig(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.VFX_CONFIG);
        toast.success(data?.message || 'Configuration updated successfully. Changes will appear on all client websites within 60 seconds.');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const usePresetBackgrounds = () => {
  return useQuery<PresetBackgroundsResponse, Error>(
    QUERY_KEYS.PRESET_BACKGROUNDS,
    () => apiClient.getPresetBackgrounds(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes - presets don't change often
    }
  );
};

// ===== VFX SCHEDULE HOOKS =====

export const useVfxSchedules = () => {
  return useQuery<VfxScheduleListResponse, Error>(
    QUERY_KEYS.VFX_SCHEDULES,
    () => apiClient.getVfxSchedules(),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useVfxSchedule = (id: string) => {
  return useQuery<VfxScheduleDetailResponse, Error>(
    [QUERY_KEYS.VFX_SCHEDULE, id],
    () => apiClient.getVfxSchedule(id),
    {
      enabled: !!id,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useCreateVfxSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation<VfxScheduleDetailResponse, Error, CreateVfxScheduleRequest>(
    (data) => apiClient.createVfxSchedule(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.VFX_SCHEDULES);
        toast.success(data?.message || 'Schedule created successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useUpdateVfxSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation<
    VfxScheduleDetailResponse,
    Error,
    { id: string; data: UpdateVfxScheduleRequest }
  >(
    ({ id, data }) => apiClient.updateVfxSchedule(id, data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(QUERY_KEYS.VFX_SCHEDULES);
        queryClient.invalidateQueries([QUERY_KEYS.VFX_SCHEDULE, variables.id]);
        toast.success(data?.message || 'Schedule updated successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useDeleteVfxSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; message: string }, Error, string>(
    (id) => apiClient.deleteVfxSchedule(id),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.VFX_SCHEDULES);
        toast.success(data?.message || 'Schedule deleted successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// ===== TRANSACTION LIMITS & FRAUD DETECTION HOOKS =====

export const useTransactionLimitsConfig = () => {
  return useQuery<TransactionLimitsConfigResponse, Error>(
    QUERY_KEYS.TRANSACTION_LIMITS_CONFIG,
    () => apiClient.getTransactionLimitsConfig(),
    {
      staleTime: 30000, // 30 seconds
      cacheTime: 300000, // 5 minutes
    }
  );
};

export const useUpdateTransactionLimitsConfig = () => {
  const queryClient = useQueryClient();

  return useMutation<TransactionLimitsConfigResponse, Error, UpdateTransactionLimitsConfigRequest>(
    (data) => apiClient.updateTransactionLimitsConfig(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.TRANSACTION_LIMITS_CONFIG);
        toast.success(data?.message || 'Configuration updated successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useFraudFlags = (filters?: FraudFlagFilters) => {
  return useQuery<FraudFlagsResponse, Error>(
    [QUERY_KEYS.FRAUD_FLAGS, filters],
    () => apiClient.getFraudFlags(filters),
    {
      keepPreviousData: true,
      staleTime: 10000, // 10 seconds
      cacheTime: 60000, // 1 minute
    }
  );
};

export const useResolveFraudFlag = () => {
  const queryClient = useQueryClient();

  return useMutation<ResolveFraudFlagResponse, Error, { flagId: string; data: ResolveFraudFlagRequest }>(
    ({ flagId, data }) => apiClient.resolveFraudFlag(flagId, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.FRAUD_FLAGS);
        toast.success(data?.message || 'Fraud flag resolved successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// ===== SIGNUP BONUS (KIOSK-317) HOOKS =====

export const useSignupBonusGrants = (filters?: SignupBonusGrantsFilters) => {
  return useQuery<SignupBonusGrantsResponse, Error>(
    [QUERY_KEYS.SIGNUP_BONUS_GRANTS, filters],
    () => apiClient.getSignupBonusGrants(filters),
    {
      keepPreviousData: true,
      staleTime: 10000,
      cacheTime: 60000,
    }
  );
};

export const useSignupBonusKpi = () => {
  return useQuery<SignupBonusKpiResponse, Error>(
    QUERY_KEYS.SIGNUP_BONUS_KPI,
    () => apiClient.getSignupBonusKpi(),
    {
      staleTime: 15000,
      cacheTime: 60000,
    }
  );
};

// ===== PROVIDER FEE CONFIG HOOKS =====

export const useProviderFeeConfigs = () => {
  return useQuery<ProviderFeeConfigsResponse, Error>(
    QUERY_KEYS.PROVIDER_FEE_CONFIGS,
    () => apiClient.getProviderFeeConfigs(),
    {
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useUpsertProviderFeeConfigs = () => {
  const queryClient = useQueryClient();
  return useMutation<ProviderFeeConfigsResponse, Error, UpsertProviderFeeConfigsRequest>(
    (data) => apiClient.upsertProviderFeeConfigs(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.PROVIDER_FEE_CONFIGS);
        toast.success(data?.message || 'Fee configuration updated successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// ===== Cashier Management (Admin) =====
export const useAdminCashiers = (
  params: {
    page?: number;
    limit?: number;
    agent_id?: string;
    shop_id?: string;
    status?: string;
    q?: string;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  },
  enabled: boolean,
) => {
  return useQuery<AdminCashiersListResponse, Error>(
    [QUERY_KEYS.ADMIN_CASHIERS, params],
    () => apiClient.getAdminCashiers(params),
    { enabled, keepPreviousData: true, staleTime: 10 * 1000 },
  );
};

export const useCreateAdminCashier = () => {
  const queryClient = useQueryClient();
  return useMutation<AdminCreateCashierResponse, Error, AdminCreateCashierRequest>(
    (body) => apiClient.createAdminCashier(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.ADMIN_CASHIERS);
        toast.success('Cashier created');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useSuspendAdminCashier = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>(
    (cashierId) => apiClient.suspendAdminCashier(cashierId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.ADMIN_CASHIERS);
        toast.success('Cashier suspended');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useUnsuspendAdminCashier = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>(
    (cashierId) => apiClient.unsuspendAdminCashier(cashierId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.ADMIN_CASHIERS);
        toast.success('Cashier reactivated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useTerminateAdminCashier = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>(
    (cashierId) => apiClient.terminateAdminCashier(cashierId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.ADMIN_CASHIERS);
        toast.success('Cashier terminated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useAdminCashierReport = (
  cashierId: string | undefined,
  params: { period?: string; from?: string; to?: string },
  enabled: boolean,
) => {
  return useQuery<AdminCashierReportResponse, Error>(
    [QUERY_KEYS.ADMIN_CASHIER_REPORT, cashierId, params],
    () => apiClient.getAdminCashierReport(cashierId!, params),
    { enabled: !!cashierId && enabled, staleTime: 10 * 1000 }
  );
};

export const useAdminCashierShifts = (
  cashierId: string | undefined,
  params: { from?: string; to?: string },
  enabled: boolean,
) => {
  return useQuery<AdminCashierShiftHistoryResponse, Error>(
    [QUERY_KEYS.ADMIN_CASHIER_SHIFTS, cashierId, params],
    () => apiClient.getAdminCashierShifts(cashierId!, params),
    { enabled: !!cashierId && enabled, staleTime: 10 * 1000 }
  );
};

export const useAdminFraudIndicators = (
  params: {
    period?: string;
    from?: string;
    to?: string;
    highRedemptionThreshold?: number;
    discrepancyThreshold?: number;
    shop_id?: string;
    agent_id?: string;
  },
  enabled: boolean,
) => {
  return useQuery<AdminFraudIndicatorsResponse, Error>(
    [QUERY_KEYS.ADMIN_FRAUD_INDICATORS, params],
    () => apiClient.getAdminFraudIndicators(params),
    { enabled, staleTime: 10 * 1000 }
  );
};

// ===== USD Wallets (gross/net) =====
export const useUsdWalletSummary = (
  params: { ownerType: string; ownerId: string; currency?: string },
  enabled: boolean,
) => {
  return useQuery(
    [QUERY_KEYS.USD_WALLET_SUMMARY, params],
    () => apiClient.getUsdWalletSummary(params),
    { enabled: !!params.ownerId && enabled, staleTime: 10 * 1000, keepPreviousData: true }
  );
};

export const useUsdWalletLedger = (
  params: {
    ownerType?: string;
    ownerId?: string;
    currency?: string;
    type?: string;
    status?: string;
    query?: string;
    transactionId?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: string;
  },
  enabled: boolean,
) => {
  return useQuery(
    [QUERY_KEYS.USD_WALLET_LEDGER, params],
    () => apiClient.getUsdWalletLedger(params),
    { enabled, staleTime: 10 * 1000, keepPreviousData: true }
  );
};

export const useUsdWalletLedgerEntry = (id: string, enabled: boolean) => {
  return useQuery(
    [QUERY_KEYS.USD_WALLET_LEDGER_ENTRY, id],
    () => apiClient.getUsdWalletLedgerEntry(id),
    { enabled: !!id && enabled, staleTime: 30 * 1000 }
  );
};

export type UsdWalletLinkedCreditsResponse = {
  success: boolean;
  data?: { rows: Record<string, unknown>[] };
  error?: string;
};

export const useUsdWalletLinkedCreditTransactions = (usdWalletTransactionId: string, enabled: boolean) => {
  return useQuery<UsdWalletLinkedCreditsResponse, Error>(
    [QUERY_KEYS.USD_WALLET_LINKED_CREDITS, usdWalletTransactionId],
    () => apiClient.getUsdWalletLinkedCreditTransactions(usdWalletTransactionId),
    {
      enabled: !!usdWalletTransactionId && enabled,
      staleTime: 30 * 1000,
    },
  );
};

export const useApproveUsdWalletWithdrawal = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data?: any; message?: string }, Error, { id: string; note?: string | null }>(
    ({ id, note }) => apiClient.approveUsdWalletWithdrawal(id, { note }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.USD_WALLET_LEDGER);
        queryClient.invalidateQueries(QUERY_KEYS.USD_WALLET_SUMMARY);
        toast.success('Approved');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useRejectUsdWalletWithdrawal = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data?: any; message?: string }, Error, { id: string; note?: string | null }>(
    ({ id, note }) => apiClient.rejectUsdWalletWithdrawal(id, { note }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.USD_WALLET_LEDGER);
        queryClient.invalidateQueries(QUERY_KEYS.USD_WALLET_SUMMARY);
        toast.success('Rejected');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// ===== Credit wallets (wallet_transactions) =====
export const useCreditWalletSummary = (
  params: { ownerType: string; ownerId: string },
  enabled: boolean,
) => {
  const ownerType = params.ownerType?.trim() ?? '';
  const ownerId = params.ownerId?.trim() ?? '';
  const canFetch = enabled && ownerType.length > 0 && ownerId.length > 0;
  return useQuery(
    [QUERY_KEYS.CREDIT_WALLET_SUMMARY, { ownerType, ownerId }],
    () => apiClient.getCreditWalletSummary({ ownerType, ownerId }),
    { enabled: canFetch, staleTime: 10 * 1000, keepPreviousData: true }
  );
};

export const useCreditWalletLedger = (
  params: {
    ownerType?: string;
    ownerId?: string;
    type?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  },
  enabled: boolean,
) => {
  return useQuery(
    [QUERY_KEYS.CREDIT_WALLET_LEDGER, params],
    () => apiClient.getCreditWalletLedger(params),
    { enabled, staleTime: 10 * 1000, keepPreviousData: true }
  );
};

// ===== MANUAL GAME DEPOSIT HOOKS =====

export const useManualGameDeposits = (filters: ManualGameDepositFilters) => {
  return useQuery<ManualGameDepositsResponse, Error>(
    [QUERY_KEYS.MANUAL_GAME_DEPOSITS, filters],
    () => apiClient.getManualGameDeposits(filters),
    {
      keepPreviousData: true,
      staleTime: 15 * 1000, // 15 seconds - pending deposits need frequent updates
      refetchInterval: filters.status === 'pending' ? 30 * 1000 : false, // Auto-refresh pending every 30s
    }
  );
};

export const useManualGameDepositDetail = (transactionId: string) => {
  return useQuery<ManualGameDepositDetailResponse, Error>(
    [QUERY_KEYS.MANUAL_GAME_DEPOSIT_DETAIL, transactionId],
    () => apiClient.getManualGameDepositDetail(transactionId),
    {
      enabled: !!transactionId,
      staleTime: 10 * 1000, // 10 seconds
      refetchInterval: 15 * 1000, // Auto-refresh every 15s for time remaining
    }
  );
};

export const useConfirmManualGameDeposit = () => {
  const queryClient = useQueryClient();

  return useMutation<ConfirmManualDepositResponse, Error, { transactionId: string; data?: ConfirmManualDepositRequest }>(
    ({ transactionId, data }) => apiClient.confirmManualGameDeposit(transactionId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.MANUAL_GAME_DEPOSITS);
        queryClient.invalidateQueries(QUERY_KEYS.MANUAL_GAME_DEPOSIT_DETAIL);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useRejectManualGameDeposit = () => {
  const queryClient = useQueryClient();

  return useMutation<RejectManualDepositResponse, Error, { transactionId: string; data: RejectManualDepositRequest }>(
    ({ transactionId, data }) => apiClient.rejectManualGameDeposit(transactionId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.MANUAL_GAME_DEPOSITS);
        queryClient.invalidateQueries(QUERY_KEYS.MANUAL_GAME_DEPOSIT_DETAIL);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useUpdateManualGameDepositAccount = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateGameAccountResponse, Error, { transactionId: string; data: UpdateGameAccountRequest }>(
    ({ transactionId, data }) => apiClient.updateManualGameDepositAccount(transactionId, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.MANUAL_GAME_DEPOSITS);
        queryClient.invalidateQueries(QUERY_KEYS.MANUAL_GAME_DEPOSIT_DETAIL);
        const action = data?.data?.action === 'created' ? 'created' : 'updated';
        toast.success(`Game account ${action} successfully`);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// ===== ABNORMAL WITHDRAWAL PROTECTION HOOKS =====

export const useWithdrawalProfitAnalysis = (transactionId: string) => {
  return useQuery<ProfitAnalysisResponse, Error>(
    [QUERY_KEYS.WITHDRAWAL_PROFIT_ANALYSIS, transactionId],
    () => apiClient.getWithdrawalProfitAnalysis(transactionId),
    {
      enabled: !!transactionId,
      staleTime: 30 * 1000, // 30 seconds
    }
  );
};

export const useAdjustUserBalance = () => {
  const queryClient = useQueryClient();

  return useMutation<AdjustBalanceResponse, Error, { userId: string; data: AdjustBalanceRequest }>(
    ({ userId, data }) => apiClient.adjustUserBalance(userId, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(QUERY_KEYS.WITHDRAWALS);
        queryClient.invalidateQueries(QUERY_KEYS.WITHDRAWAL_PROFIT_ANALYSIS);
        toast.success(data?.message || SUCCESS_MESSAGES.BALANCE_ADJUSTED);
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const usePlayerAutoRefundSummary = (userId: string) => {
  return useQuery<PlayerAutoRefundSummaryResponse, Error>(
    [QUERY_KEYS.PLAYER_AUTO_REFUND_SUMMARY, userId],
    () => apiClient.getPlayerAutoRefundSummary(userId),
    {
      enabled: !!userId,
      staleTime: 30 * 1000,
    }
  );
};

export const useClearPlayerAutoRefundWarning = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ClearAutoRefundWarningResponse,
    Error,
    { userId: string; data?: ClearAutoRefundWarningRequest }
  >(
    ({ userId, data }) => apiClient.clearPlayerAutoRefundWarning(userId, data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([QUERY_KEYS.PLAYER_AUTO_REFUND_SUMMARY, variables.userId]);
        queryClient.invalidateQueries([QUERY_KEYS.USER_DETAILS, variables.userId]);
        toast.success(data?.message || 'Auto refund warning cleared');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// ─── Domain Hooks ─────────────────────────────────────────────────────────────

export const useDomains = () => {
  return useQuery<DomainsResponse, Error>(
    QUERY_KEYS.DOMAINS,
    () => apiClient.getDomains(),
    { staleTime: 30 * 1000 }
  );
};

export const useCreateDomain = () => {
  const queryClient = useQueryClient();
  return useMutation<DomainResponse, Error, CreateDomainRequest>(
    (data) => apiClient.createDomain(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.DOMAINS);
        toast.success('Domain created successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useUpdateDomain = () => {
  const queryClient = useQueryClient();
  return useMutation<DomainResponse, Error, { id: string; data: UpdateDomainRequest }>(
    ({ id, data }) => apiClient.updateDomain(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.DOMAINS);
        toast.success('Domain updated successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useDeleteDomain = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; message: string }, Error, string>(
    (id) => apiClient.deleteDomain(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.DOMAINS);
        toast.success('Domain deleted successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useDomainGameMappings = (domainId: string | null, enabled = true) => {
  return useQuery<DomainGameMappingsResponse, Error>(
    [QUERY_KEYS.DOMAIN_GAME_MAPPINGS, domainId],
    () => apiClient.getDomainGameMappings(domainId!),
    { enabled: !!domainId && enabled, staleTime: 15 * 1000 }
  );
};

export const useReplaceDomainGameMappings = () => {
  const queryClient = useQueryClient();
  return useMutation<DomainGameMappingsResponse, Error, { domainId: string; gameIds: string[] }>(
    ({ domainId, gameIds }) => apiClient.replaceDomainGameMappings(domainId, gameIds),
    {
      onSuccess: (_data, { domainId }) => {
        queryClient.invalidateQueries([QUERY_KEYS.DOMAIN_GAME_MAPPINGS, domainId]);
        queryClient.invalidateQueries(QUERY_KEYS.DOMAINS);
        toast.success('Domain games updated successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

// ─── Kiosk Health Hooks ───────────────────────────────────────────────────────

export const useKioskHealth = () => {
  return useQuery<KioskHealthResponse, Error>(
    QUERY_KEYS.KIOSK_HEALTH,
    () => apiClient.getKioskHealth(),
    { staleTime: 15 * 1000, refetchInterval: 30 * 1000 }
  );
};

// ─── Domain Analytics Hooks ───────────────────────────────────────────────────

export const useDomainAnalytics = (domainId: string | null) => {
  return useQuery<DomainAnalyticsResponse, Error>(
    [QUERY_KEYS.DOMAIN_ANALYTICS, domainId],
    () => apiClient.getDomainAnalytics(domainId!),
    { enabled: !!domainId, staleTime: 60 * 1000 }
  );
};

// ─── CDN Agent Management ───────────────────────────────────────────────────

export const useCdnAgents = (enabled: boolean) => {
  return useQuery<AgentsListResponse, Error>(
    QUERY_KEYS.CDN_AGENTS,
    () => apiClient.getCdnAgents(),
    { enabled, staleTime: 30 * 1000 }
  );
};

export const useSuperAgents = (enabled: boolean) => {
  return useQuery<SuperAgentsListResponse, Error>(
    QUERY_KEYS.SUPER_AGENTS,
    () => apiClient.getSuperAgents(),
    { enabled, staleTime: 30 * 1000 }
  );
};

export const useSuperAgentDetail = (superAgentId: string | undefined, enabled: boolean) => {
  return useQuery(
    [QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId],
    () => apiClient.getSuperAgentDetail(superAgentId!),
    { enabled: !!superAgentId && enabled, staleTime: 15 * 1000 }
  );
};

export const useSuperAgentWalletTransactions = (
  superAgentId: string | undefined,
  filters: { page?: number; limit?: number },
  enabled: boolean,
) => {
  return useQuery<SuperAgentWalletTransactionsResponse, Error>(
    [QUERY_KEYS.SUPER_AGENT_WALLET_TRANSACTIONS, superAgentId, filters],
    () => apiClient.getSuperAgentWalletTransactions(superAgentId!, filters),
    { enabled: !!superAgentId && enabled, staleTime: 15 * 1000, keepPreviousData: true },
  );
};

export const useSuperAgentGameMappings = (superAgentId: string | undefined, enabled: boolean) => {
  return useQuery(
    [QUERY_KEYS.SUPER_AGENT_GAME_MAPPINGS, superAgentId],
    () => apiClient.getSuperAgentGameMappings(superAgentId!),
    { enabled: !!superAgentId && enabled, staleTime: 30 * 1000 }
  );
};

export const usePutSuperAgentGameMappings = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data?: unknown },
    Error,
    { superAgentId: string; game_ids: string[] }
  >(({ superAgentId, game_ids }) => apiClient.putSuperAgentGameMappings(superAgentId, game_ids), {
    onSuccess: (_res, { superAgentId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_GAME_MAPPINGS, superAgentId]);
      queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
      toast.success('Game list updated');
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
  });
};

export const useCreateSuperAgent = () => {
  const queryClient = useQueryClient();
  return useMutation<CreateSuperAgentResponse, Error, CreateSuperAgentRequest>(
    (data) => apiClient.createSuperAgent(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
        toast.success('Super agent created successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const usePatchSuperAgentRates = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: unknown },
    Error,
    { superAgentId: string; cost_rate: number }
  >(({ superAgentId, cost_rate }) => apiClient.patchSuperAgentRates(superAgentId, cost_rate), {
    onSuccess: (_res, { superAgentId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
      queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
      toast.success('Cost rate updated');
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
  });
};

export const usePatchSuperAgentName = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: unknown },
    Error,
    { superAgentId: string; name: string }
  >(({ superAgentId, name }) => apiClient.patchSuperAgent(superAgentId, { name }), {
    onSuccess: (_res, { superAgentId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
      queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
      toast.success('Display name updated');
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
  });
};

export const usePatchSuperAgent = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: unknown },
    Error,
    {
      superAgentId: string;
      can_delegate_popups?: boolean;
      config_level?: 'shop' | 'agent' | 'super_agent';
    }
  >(
    ({ superAgentId, can_delegate_popups, config_level }) =>
      apiClient.patchSuperAgent(superAgentId, { can_delegate_popups, config_level }),
    {
    onSuccess: (_res, { superAgentId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
      queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
      toast.success('Super agent settings updated');
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
    }
  );
};

export const usePatchSuperAgentPortalWithdrawalLimits = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: unknown },
    Error,
    {
      superAgentId: string;
      max_portal_withdrawal_per_transaction?: number | null;
      max_portal_withdrawal_per_day?: number | null;
    }
  >(
    ({ superAgentId, max_portal_withdrawal_per_transaction, max_portal_withdrawal_per_day }) =>
      apiClient.patchSuperAgent(superAgentId, {
        max_portal_withdrawal_per_transaction,
        max_portal_withdrawal_per_day,
      }),
    {
      onSuccess: (_res, { superAgentId }) => {
        queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
        toast.success('Withdrawal limits updated. They apply to all agents and shops under this super agent.');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const usePatchSuperAgentPlayerWithdrawalAutoApprove = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: unknown },
    Error,
    { superAgentId: string; withdrawal_auto_approve_threshold: number | null }
  >(
    ({ superAgentId, withdrawal_auto_approve_threshold }) =>
      apiClient.patchSuperAgent(superAgentId, { withdrawal_auto_approve_threshold }),
    {
      onSuccess: (_res, { superAgentId }) => {
        queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
        toast.success('Player auto-approve threshold updated.');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const usePatchSuperAgentPassword = () => {
  return useMutation<{ success: boolean; message?: string }, Error, { superAgentId: string; password: string }>(
    ({ superAgentId, password }) => apiClient.patchSuperAgentPassword(superAgentId, { password }),
    {
      onSuccess: (res) => {
        toast.success(res?.message || 'Super agent password updated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const useSuspendAdminSuperAgent = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data: unknown }, Error, string>(
    (superAgentId) => apiClient.suspendAdminSuperAgent(superAgentId),
    {
      onSuccess: (_res, superAgentId) => {
        queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
        queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        toast.success('Super agent suspended');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const useUnsuspendAdminSuperAgent = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data: unknown }, Error, string>(
    (superAgentId) => apiClient.unsuspendAdminSuperAgent(superAgentId),
    {
      onSuccess: (_res, superAgentId) => {
        queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
        queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        toast.success('Super agent reactivated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const useTerminateAdminSuperAgent = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data: unknown }, Error, string>(
    (superAgentId) => apiClient.terminateAdminSuperAgent(superAgentId),
    {
      onSuccess: (_res, superAgentId) => {
        queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
        queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        toast.success('Super agent terminated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const useAdminSuperAgentCashFunding = () => {
  const queryClient = useQueryClient();
  return useMutation<
    AdminAgentCashFundingResponse,
    Error,
    { superAgentId: string; body: AdminAgentCashFundingRequest }
  >(({ superAgentId, body }) => apiClient.postAdminSuperAgentCashFunding(superAgentId, body), {
    onSuccess: (res, { superAgentId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
      queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_WALLET_TRANSACTIONS, superAgentId]);
      queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
      const bal = res?.data?.new_balance;
      toast.success(
        typeof bal === 'number' ? `Credits added. Super agent balance: ${bal}` : 'Cash funding recorded',
      );
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
  });
};

export const useAdminSuperAgentCashWithdrawal = () => {
  const queryClient = useQueryClient();
  return useMutation<
    AdminAgentCashFundingResponse,
    Error,
    { superAgentId: string; body: AdminAgentCashFundingRequest }
  >(({ superAgentId, body }) => apiClient.postAdminSuperAgentCashWithdrawal(superAgentId, body), {
    onSuccess: (res, { superAgentId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_DETAIL, superAgentId]);
      queryClient.invalidateQueries([QUERY_KEYS.SUPER_AGENT_WALLET_TRANSACTIONS, superAgentId]);
      queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
      const bal = res?.data?.new_balance;
      toast.success(
        typeof bal === 'number' ? `Credits withdrawn. Super agent balance: ${bal}` : 'Cash withdrawal recorded',
      );
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
  });
};

export const useAssignAgentToSuperAgent = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: CdnAgent },
    Error,
    { superAgentId: string; agentId: string }
  >(
    ({ superAgentId, agentId }) => apiClient.assignAgentToSuperAgent(superAgentId, agentId),
    {
      onSuccess: (_res, { agentId }) => {
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
        queryClient.invalidateQueries(QUERY_KEYS.SUPER_AGENTS);
        toast.success('Assigned to super agent');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useCreateCdnAgent = () => {
  const queryClient = useQueryClient();
  return useMutation<CreateCdnAgentResponse, Error, CreateCdnAgentRequest>(
    (data) => apiClient.createCdnAgent(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        toast.success('Agent created successfully');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useSuspendCdnAgent = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data: CreateCdnAgentResponse['data'] }, Error, string>(
    (agentId) => apiClient.suspendCdnAgent(agentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        toast.success('Agent suspended');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useUnsuspendCdnAgent = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data: CreateCdnAgentResponse['data'] }, Error, string>(
    (agentId) => apiClient.unsuspendCdnAgent(agentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        toast.success('Agent reactivated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useTerminateCdnAgent = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data: unknown }, Error, string>(
    (agentId) => apiClient.terminateCdnAgent(agentId),
    {
      onSuccess: (_data, agentId) => {
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
        toast.success('Agent terminated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useAgentTerminatePreview = (agentId: string | undefined, enabled: boolean) => {
  return useQuery(
    [QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId, 'terminate-preview'],
    () => apiClient.getAgentTerminatePreview(agentId!),
    {
      enabled: enabled && !!agentId,
      staleTime: 0,
    },
  );
};

export const useAdminAgentDetail = (agentId: string | undefined, enabled: boolean) => {
  return useQuery<AdminAgentDetailResponse, Error>(
    [QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId],
    () => apiClient.getAdminAgentDetail(agentId!),
    { enabled: !!agentId && enabled, staleTime: 15 * 1000 },
  );
};

export const usePatchAdminAgentPopupPermissions = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: CdnAgent },
    Error,
    {
      agentId: string;
      body: {
        can_create_shop_popup?: boolean;
        can_manage_shop_popup_permission?: boolean;
        default_shop_can_create_player_popup?: boolean;
      };
    }
  >(({ agentId, body }) => apiClient.patchAdminAgentPopupPermissions(agentId, body), {
    onSuccess: (_res, { agentId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
      queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
      toast.success('Popup permissions updated');
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
  });
};

export const usePatchAdminShopPopupPermissions = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: CdnShop },
    Error,
    { agentId: string; shopId: string; body: { can_create_player_popup: boolean } }
  >(({ agentId, shopId, body }) => apiClient.patchAdminShopPopupPermissions(agentId, shopId, body), {
    onSuccess: (_res, { agentId, shopId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL, agentId, shopId]);
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
      queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
      toast.success('Shop popup permission updated');
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
  });
};

export const useAdminAgentPaymentTransactions = (
  agentId: string | undefined,
  enabled: boolean,
  params: { page: number; limit: number; status?: string; type?: string; provider?: string },
) => {
  return useQuery<AdminAgentPaymentTransactionsResponse, Error>(
    [QUERY_KEYS.ADMIN_AGENT_PAYMENT_TX, agentId, params],
    () => apiClient.getAdminAgentPaymentTransactions(agentId!, params),
    { enabled: !!agentId && enabled, staleTime: 10 * 1000 },
  );
};

export const useAdminAgentWalletTransactions = (
  agentId: string | undefined,
  enabled: boolean,
  params: { page: number; limit: number; type?: string; shop_id?: string },
) => {
  return useQuery<AdminAgentWalletTransactionsResponse, Error>(
    [QUERY_KEYS.ADMIN_AGENT_WALLET_TX, agentId, params],
    () => apiClient.getAdminAgentWalletTransactions(agentId!, params),
    { enabled: !!agentId && enabled, staleTime: 10 * 1000 },
  );
};

export const useCostReport = (
  enabled: boolean,
  params: {
    agent_id?: string;
    from?: string;
    to?: string;
    event_type?: 'shop_direct_topup' | 'user_direct_topup';
    page?: number;
    limit?: number;
  },
) => {
  return useQuery<CostReportResponse, Error>(
    [QUERY_KEYS.COST_REPORT, params],
    () => apiClient.getCostReport(params),
    { enabled, keepPreviousData: true, staleTime: 15 * 1000 },
  );
};

export const useCreateAdminAgentShop = () => {
  const queryClient = useQueryClient();
  return useMutation<CreateCdnShopResponse, Error, { agentId: string; data: CreateCdnShopRequest }>(
    ({ agentId, data }) => apiClient.createAdminAgentShop(agentId, data),
    {
      onSuccess: (_res, { agentId }) => {
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        queryClient.invalidateQueries({ queryKey: ['adminShops'] });
        toast.success('Shop created');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const useAdminShopDetail = (agentId: string | undefined, shopId: string | undefined, enabled: boolean) => {
  return useQuery<AdminShopDetailResponse, Error>(
    [QUERY_KEYS.ADMIN_SHOP_DETAIL, agentId, shopId],
    () => apiClient.getAdminAgentShopDetail(agentId!, shopId!),
    { enabled: !!agentId && !!shopId && enabled, staleTime: 15 * 1000 },
  );
};

export const useAdminShopDetailByShopId = (shopId: string | undefined, enabled: boolean) => {
  return useQuery<AdminShopDetailResponse, Error>(
    [QUERY_KEYS.ADMIN_SHOP_DETAIL_BY_SHOP_ID, shopId],
    () => apiClient.getAdminShopDetailByShopId(shopId!),
    { enabled: !!shopId && enabled, staleTime: 15 * 1000 },
  );
};

export const usePatchAdminShopRates = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: CdnShop },
    Error,
    { agentId: string; shopId: string; cost_rate: number }
  >(({ agentId, shopId, cost_rate }) => apiClient.patchAdminShopRates(agentId, shopId, cost_rate), {
    onSuccess: (_res, { agentId, shopId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL, agentId, shopId]);
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
      toast.success('Cost rate updated');
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
  });
};

export const usePostAdminShopCreditAdjust = () => {
  const queryClient = useQueryClient();
  return useMutation<
    AdminShopCreditAdjustResponse,
    Error,
    { agentId: string; shopId: string; body: AdminShopCreditAdjustRequest }
  >(({ agentId, shopId, body }) => apiClient.postAdminShopCreditAdjust(agentId, shopId, body), {
    onSuccess: (_res, { agentId, shopId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL, agentId, shopId]);
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
      toast.success('Balance adjusted');
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
  });
};

export const usePostAdminFinanceCorrection = () => {
  return useMutation<AdminFinanceCorrectionResponse, Error, AdminFinanceCorrectionRequest>(
    (body) => apiClient.postAdminFinanceCorrection(body),
    {
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const useSuspendAdminShop = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data: CdnShop }, Error, { agentId: string; shopId: string }>(
    ({ agentId, shopId }) => apiClient.suspendAdminShop(agentId, shopId),
    {
      onSuccess: (_res, { agentId, shopId }) => {
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL, agentId, shopId]);
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL_BY_SHOP_ID, shopId]);
        toast.success('Shop suspended');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const useUnsuspendAdminShop = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data: CdnShop }, Error, { agentId: string; shopId: string }>(
    ({ agentId, shopId }) => apiClient.unsuspendAdminShop(agentId, shopId),
    {
      onSuccess: (_res, { agentId, shopId }) => {
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL, agentId, shopId]);
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL_BY_SHOP_ID, shopId]);
        toast.success('Shop reactivated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const useTerminateAdminShop = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; data: unknown }, Error, { agentId: string; shopId: string }>(
    ({ agentId, shopId }) => apiClient.terminateAdminShop(agentId, shopId),
    {
      onSuccess: (_res, { agentId, shopId }) => {
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
        queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL_BY_SHOP_ID, shopId]);
        toast.success('Shop terminated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const usePatchAdminAgentPassword = () => {
  return useMutation<{ success: boolean; message?: string }, Error, { agentId: string; password: string }>(
    ({ agentId, password }) => apiClient.patchAdminAgentPassword(agentId, { password }),
    {
      onSuccess: (res) => {
        toast.success(res?.message || 'Agent password updated');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    },
  );
};

export const usePatchAdminAgentRates = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: CdnAgent },
    Error,
    { agentId: string; cost_rate: number }
  >(({ agentId, cost_rate }) => apiClient.patchAdminAgentRates(agentId, cost_rate), {
    onSuccess: (_res, { agentId }) => {
      queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
      queryClient.invalidateQueries(QUERY_KEYS.CDN_AGENTS);
      toast.success('Cost rate updated');
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error));
    },
  });
};

export const usePatchAdminAgentPlayerWithdrawalAutoApprove = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: CdnAgent },
    Error,
    { agentId: string; withdrawal_auto_approve_threshold: number | null }
  >(
    ({ agentId, withdrawal_auto_approve_threshold }) =>
      apiClient.patchAdminAgentPlayerWithdrawalAutoApprove(agentId, withdrawal_auto_approve_threshold),
    {
      onSuccess: (_res, { agentId }) => {
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
        toast.success('Player auto-approve threshold updated.');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const usePatchAdminShopPlayerWithdrawalAutoApprove = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; data: CdnShop },
    Error,
    { agentId: string; shopId: string; withdrawal_auto_approve_threshold: number | null }
  >(
    ({ agentId, shopId, withdrawal_auto_approve_threshold }) =>
      apiClient.patchAdminShopPlayerWithdrawalAutoApprove(agentId, shopId, withdrawal_auto_approve_threshold),
    {
      onSuccess: (_res, { agentId, shopId }) => {
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL, agentId, shopId]);
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_SHOP_DETAIL_BY_SHOP_ID, shopId]);
        queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
        toast.success('Player auto-approve threshold updated.');
      },
      onError: (error) => {
        toast.error(parseErrorMessage(error));
      },
    }
  );
};

export const useIntegrationGameLogs = (
  params: Record<string, string | number | undefined>,
  enabled = true,
) => {
  return useQuery(
    [QUERY_KEYS.INTEGRATION_GAME_LOGS, params],
    () => apiClient.getIntegrationGameLogs(params),
    { enabled: enabled && !!params.from && !!params.to, staleTime: 30_000 },
  );
};

export const useAdminAuditLogs = (
  params: Record<string, string | number | undefined>,
  enabled = true,
) => {
  return useQuery(
    [QUERY_KEYS.AUDIT_LOGS, params],
    () => apiClient.getAdminAuditLogs(params),
    { enabled, staleTime: 15_000 },
  );
};

export const useIntegrationPaymentLogs = (
  params: Record<string, string | number | undefined>,
  enabled = true,
) => {
  return useQuery(
    [QUERY_KEYS.INTEGRATION_PAYMENT_LOGS, params],
    () => apiClient.getIntegrationPaymentLogs(params),
    { enabled: enabled && !!params.from && !!params.to, staleTime: 30_000 },
  );
};

export const useIntegrationWebhookLogs = (
  params: Record<string, string | number | undefined>,
  enabled = true,
) => {
  return useQuery(
    [QUERY_KEYS.INTEGRATION_WEBHOOK_LOGS, params],
    () => apiClient.getIntegrationWebhookLogs(params),
    { enabled: enabled && !!params.from && !!params.to, staleTime: 30_000 },
  );
};

export const useIntegrationGameLogDetail = (id: string | null) => {
  return useQuery(
    [QUERY_KEYS.INTEGRATION_GAME_LOG_DETAIL, id],
    () => apiClient.getIntegrationGameLogDetail(id!),
    { enabled: !!id },
  );
};

export const useIntegrationPaymentLogDetail = (id: string | null) => {
  return useQuery(
    [QUERY_KEYS.INTEGRATION_PAYMENT_LOG_DETAIL, id],
    () => apiClient.getIntegrationPaymentLogDetail(id!),
    { enabled: !!id },
  );
};

export const useIntegrationWebhookLogDetail = (id: string | number | null) => {
  return useQuery(
    [QUERY_KEYS.INTEGRATION_WEBHOOK_LOG_DETAIL, id],
    () => apiClient.getIntegrationWebhookLogDetail(id!),
    { enabled: id != null && id !== '' },
  );
};

